import { FC, useMemo, useState } from 'react';
import { generalHelpers, newAttendance } from '@monorepo/helpers';
import { helpers } from 'src/services';
import { HorizontalSelector } from 'src/components/_commons';
import { typeMeeting } from '@monorepo/models';
import { useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    justFuture: boolean;
    meetings: typeMeeting[];
};

export const MeetingAssignmentsTable: FC<propsType> = ({ justFuture, meetings }) => {
    const [weekNumber, setWeekNumber] = useState(generalHelpers.GetCalendarWeekNumber(new Date()));
    const [year, setYear] = useState(new Date().getFullYear());
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();

    const getEmptyMeeting = (_year: number, _weekNumber: number): typeMeeting => {
        const range = helpers.GetCalendarWeekRange(_weekNumber, _year);
        return {
            attendance: newAttendance,
            avAssignments: [],
            date: `${range.minDateStr} - ${range.maxDateStr} ${year}`,
            groupsForCleaning: [],
            id: 0,
            sections: [],
            type: null,
            usherAssignments: [],
            weekday: 0,
            weekNumber: _weekNumber,
            year: _year
        };
    }

    const sample: typeMeeting[] = useMemo(() => {
        const weekCountPrevYear = generalHelpers.GetCalendarWeekCount(year - 1);
        const meeting1 = meetings.find(m => weekNumber === 1 ?
            m.year === year - 1 && m.weekNumber === weekCountPrevYear - 1
            :
            weekNumber === 2 ?
                m.year === year - 1 && m.weekNumber === weekCountPrevYear
                :
                m.year === year && m.weekNumber === weekNumber - 2
        ) ?? getEmptyMeeting(weekNumber <= 2 ? year - 1 : year, weekNumber === 1 ? weekCountPrevYear - 1 : weekNumber === 2 ? weekCountPrevYear : weekNumber - 2);
        const meeting2 = meetings.find(m => weekNumber === 1 ?
            m.year === year - 1 && m.weekNumber === weekCountPrevYear
            :
            m.year === year && m.weekNumber === weekNumber - 1
        ) ?? getEmptyMeeting(weekNumber === 1 ? year - 1 : year, weekNumber === 1 ? weekCountPrevYear : weekNumber - 1);
        const meeting3 = meetings.find(m =>
            m.year === year && m.weekNumber === weekNumber
        ) ?? getEmptyMeeting(year, weekNumber);
        const meeting4 = meetings.find(m => weekNumber === weekCountPrevYear ?
            m.year === year + 1 && m.weekNumber === 1
            :
            m.year === year && m.weekNumber === weekNumber + 1
        ) ?? getEmptyMeeting(weekNumber === weekCountPrevYear ? year + 1 : year, weekNumber === weekCountPrevYear ? 1 : weekNumber + 1);
        const meeting5 = meetings.find(m => weekNumber === weekCountPrevYear - 1 ?
            m.year === year + 1 && m.weekNumber === 1
            :
            weekNumber === weekCountPrevYear ?
                m.year === year + 1 && m.weekNumber === 2
                :
                m.year === year && m.weekNumber === weekNumber + 2
        ) ?? getEmptyMeeting(weekNumber >= weekCountPrevYear - 1 ? year + 1 : year, weekNumber === weekCountPrevYear - 1 ? 1 : weekNumber === weekCountPrevYear ? 2 : weekNumber + 2);
        const meeting6 = meetings.find(m => weekNumber === weekCountPrevYear - 2 ?
            m.year === year + 1 && m.weekNumber === 1
            :
            weekNumber === weekCountPrevYear - 1 ?
                m.year === year + 1 && m.weekNumber === 2
                :
                weekNumber === weekCountPrevYear ?
                    m.year === year + 1 && m.weekNumber === 3
                    :
                    m.year === year && m.weekNumber === weekNumber + 3
        ) ?? getEmptyMeeting(weekNumber >= weekCountPrevYear - 2 ? year + 1 : year, weekNumber === weekCountPrevYear - 2 ? 1 : weekNumber === weekCountPrevYear - 1 ? 2 : weekNumber === weekCountPrevYear ? 3 : weekNumber + 3);
        if (justFuture) {
            const _sample = [meeting3, meeting4, meeting5, meeting6];
            return _sample;
        } else {
            const _sample = [meeting1, meeting2, meeting3, meeting4, meeting5];
            return _sample;
        }
    }, [justFuture, meetings, weekNumber, year]);

    const meetingsToRender: typeMeeting[] = useMemo(() => {
        const filtered: typeMeeting[] = sample.map(m => ({
            ...m,
            date: m.date.includes(' - ') ?
                m.date
                :
                `${m.date.split('-')[2]}-${m.date.split('-')[1]}-${m.date.split('-')[0]}`,
            flattedAssignments: [],
            sections: m.sections.filter(s =>
                s.segments.some(se => se.assignments.some(a => a.user?.id && a.room === '1' && !a.isHelper))
            )
        }));
        filtered.forEach(m => {
            m.sections.forEach(s => {
                s.segments.forEach(se => {
                    const assignment = se.assignments.find(a => a.user?.id && a.room === '1' && !a.isHelper);
                    const assignmentHelper = se.assignments.find(a => a.user?.id && a.room === '1' && a.isHelper);
                    const assignmentAuxiliarRoom = se.assignments.find(a => a.user?.id && a.room === '2' && !a.isHelper);
                    const assignmentAuxiliarRoomHelper = se.assignments.find(a => a.user?.id && a.room === '2' && a.isHelper);
                    if (assignment || assignmentHelper || assignmentAuxiliarRoom || assignmentAuxiliarRoomHelper) {
                        m.flattedAssignments!.push({
                            date: m.date,
                            subtitle: se.subtitle.toLowerCase().includes('palabras de introducción') && se.subtitle.toLowerCase().includes('oración') ?
                                'Oración Inicial'
                                :
                                se.subtitle,
                            user: assignment?.user!,
                            userHelper: assignmentHelper?.user ?? null,
                            userAuxiliarRoom: assignmentAuxiliarRoom?.user!,
                            userAuxiliarRoomHelper: assignmentAuxiliarRoomHelper?.user ?? null,
                        });
                    }
                });
            });
        });
        return filtered;
    }, [sample]);
    
    return (
        <div className={isDarkMode ? 'bg-dark' : ''}>
            {!justFuture &&
                <div className={'py-4'}>
                    <HorizontalSelector
                        label={'SEMANAS'}
                        setPrevious={() => {
                            if (weekNumber === 1) {
                                const weeksInPreviousYear = generalHelpers.GetCalendarWeekCount(year - 1);  // 52-53 weeks
                                setWeekNumber(weeksInPreviousYear);
                                setYear(x => x - 1);
                            } else {
                                setWeekNumber(x => x - 1);
                            }
                        }}
                        setNext={() => {
                            const weeksInCurrentYear = generalHelpers.GetCalendarWeekCount(year);  // 52-53 weeks
                            if (weekNumber === weeksInCurrentYear) {
                                setWeekNumber(1);
                                setYear(x => x + 1);
                            } else {
                                setWeekNumber(x => x + 1);
                            }
                        }}
                    />
                </div>
            }
            <div className={'table-responsive'}>
                <table className={`table table-striped table-bordered ${isDarkMode ? 'table-dark' : ''} small w-100 mx-auto mb-0`}
                    style={{ tableLayout: 'fixed' }}
                >
                    <thead>
                        <tr>
                            <th className={`${isMobile ? '' : 'h5 text-nowrap'} text-center`}>
                                {isMobile ? meetingsToRender.at(0)?.date.slice(0, -5) : meetingsToRender.at(0)?.date}
                            </th>
                            <th className={`${isMobile ? '' : 'h5 text-nowrap'} text-center`}>
                                {isMobile ? meetingsToRender.at(1)?.date.slice(0, -5) : meetingsToRender.at(1)?.date}
                            </th>
                            <th className={`${isMobile ? '' : 'h5 text-nowrap'} text-center`}>
                                {isMobile ? meetingsToRender.at(2)?.date.slice(0, -5) : meetingsToRender.at(2)?.date}
                            </th>
                            <th className={`${isMobile ? '' : 'h5 text-nowrap'} text-center`}>
                                {isMobile ? meetingsToRender.at(3)?.date.slice(0, -5) : meetingsToRender.at(3)?.date}
                            </th>
                            {meetingsToRender.length === 5 &&
                                <th className={`${isMobile ? '' : 'h5 text-nowrap'} text-center`}>
                                    {isMobile ? meetingsToRender.at(4)?.date.slice(0, -5) : meetingsToRender.at(4)?.date}
                                </th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {Array
                            .from({ length: Math.max(...meetingsToRender.map(m => m.flattedAssignments?.length ?? 0)) }, (_, i) => i)
                            .map(n =>
                            <tr key={n}>
                                <td>
                                    {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.subtitle}
                                    {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.user &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.user?.name} {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.user?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Ayudante: {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userHelper?.name} {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userAuxiliarRoom &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar: {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.name} {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar Ayudante: {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.name} {meetingsToRender.at(0)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                </td>
                                <td>
                                    {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.subtitle}
                                    {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.user &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.user?.name} {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.user?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Ayudante: {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userHelper?.name} {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userAuxiliarRoom &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar: {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.name} {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar Ayudante: {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.name} {meetingsToRender.at(1)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                </td>
                                <td>
                                    {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.subtitle}
                                    {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.user &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.user?.name} {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.user?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Ayudante: {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userHelper?.name} {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userAuxiliarRoom &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar: {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.name} {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar Ayudante: {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.name} {meetingsToRender.at(2)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                </td>
                                <td>
                                    {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.subtitle}
                                    {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.user &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.user?.name} {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.user?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Ayudante: {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userHelper?.name} {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userAuxiliarRoom &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar: {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.name} {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.lastName}
                                        </span>
                                        </>
                                    }
                                    {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper &&
                                        <>
                                        <br />
                                        <span className={'fw-bold'}>
                                            Sala Auxiliar Ayudante: {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.name} {meetingsToRender.at(3)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.lastName}
                                        </span>
                                        </>
                                    }
                                </td>
                                {meetingsToRender.length === 5 &&
                                    <td>
                                        {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.subtitle}
                                        {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.user &&
                                            <>
                                            <br />
                                            <span className={'fw-bold'}>
                                                {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.user?.name} {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.user?.lastName}
                                            </span>
                                            </>
                                        }
                                        {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userHelper &&
                                            <>
                                            <br />
                                            <span className={'fw-bold'}>
                                                Ayudante: {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userHelper?.name} {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userHelper?.lastName}
                                            </span>
                                            </>
                                        }
                                        {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userAuxiliarRoom &&
                                            <>
                                            <br />
                                            <span className={'fw-bold'}>
                                                Sala Auxiliar: {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.name} {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userAuxiliarRoom?.lastName}
                                            </span>
                                            </>
                                        }
                                        {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper &&
                                            <>
                                            <br />
                                            <span className={'fw-bold'}>
                                                Sala Auxiliar Ayudante: {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.name} {meetingsToRender.at(4)?.flattedAssignments?.at(n)?.userAuxiliarRoomHelper?.lastName}
                                            </span>
                                            </>
                                        }
                                    </td>
                                }
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
